import AnyCard from 'components/Card'
import ContentTypeFilter from 'components/ContentTypeFilter'
import { MasonryGrid } from 'components/ContentGridView'
import EmptyView from 'components/EmptyView'
import RangeFilterSelect from 'components/RangeFilterSelect'
import ScrollListener from 'components/ScrollListener'
import Loader from 'components/SimpleLoader'
import { ARTICLE_TYPE, PHOTO_TYPE, RangeFilter, VIDEO_TYPE } from 'interfaces'
import { TEXT_TYPE } from 'interfaces/Content/ContentType'
import {
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import { FormattedMessage } from 'react-intl'
import { NavLink } from 'react-router'
import { renderLoadingCards } from 'routes/find/routes/home/components/utils'
import { updateState } from 'routes/search/state/actions'
import { SearchContext } from 'routes/search/state/context'
import { SEARCH_CATEGORY_CONTENT, SEARCH_FILTER_KEY_THREADS, SearchFilterKey } from 'routes/search/state/types'
import { PAGE_SIZE_DEFAULT } from 'services/content/util'
import { resetScroll } from 'utils/dom'
import styles from '../SearchStockRoute/SearchStockRoute.pcss'
import threadsStyles from './SearchThreadsRoute.pcss'

const SCROLL_THRESHOLD = 350
const LOADER_SIZE = 60
const THREADS_FILTERS = [TEXT_TYPE, ARTICLE_TYPE, VIDEO_TYPE, PHOTO_TYPE]

export function SearchThreadsRoute() {
  const [{ query, activeFilter, results, threadsType, threadsRange }, searchDispatch] = useContext(SearchContext)
  const { items, loading } = results[activeFilter as SearchFilterKey] || {}
  const [page, setPage] = useState(0)
  const scrollElementRef = useRef<HTMLElement | undefined>(undefined)

  useLayoutEffect(() => {
    scrollElementRef.current = document.querySelector('[data-test="main"]') as HTMLElement
    resetScroll()
  }, [])

  useEffect(() => {
    searchDispatch(updateState({
      activeFilter: SEARCH_FILTER_KEY_THREADS,
      activeCategory: SEARCH_CATEGORY_CONTENT
    }))
  }, [searchDispatch])

  const loadingCards = useMemo(() => (loading ? renderLoadingCards() : []), [loading])

  const pageStartIndex = page * PAGE_SIZE_DEFAULT
  const allItems = items || []

  const visibleItems = allItems.slice(0, pageStartIndex + PAGE_SIZE_DEFAULT)

  const emptyCards = useMemo(() => {
    if (!loading && visibleItems.length === 0) {
      // eslint-disable-next-line no-magic-numbers
      return renderLoadingCards(30, false, false, false)
    }
    return []
  }, [visibleItems.length, loading])

  if (!activeFilter) {
    return null
  }

  const onTypeSelected = (type: string) => {
    searchDispatch(updateState({ threadsType: type }))
    setPage(0)
  }

  const onRangeSelected = (range: RangeFilter) => {
    searchDispatch(updateState({ threadsRange: range }))
    setPage(0)
  }

  const onLoadNextPage = () => {
    if (!loading && visibleItems.length < allItems.length) {
      setPage(current => current + 1)
    }
  }

  return (
    <div className={styles.box}>
      <header className={styles.header}>
        <h2 className="text-ellipsis">
          <FormattedMessage id={`search.navigation.${SEARCH_FILTER_KEY_THREADS}`} />
        </h2>
        <NavLink to={`/content/search?q=${query}`}>
          <FormattedMessage id="general.carousel.nav.back" />
        </NavLink>
        <div className={styles.filters}>
          <ContentTypeFilter
            options={THREADS_FILTERS}
            selected={threadsType}
            onFilterSelected={onTypeSelected}
          />
          <RangeFilterSelect
            range={threadsRange}
            onRangeSelected={onRangeSelected}
          />
        </div>
      </header>
      {!loading && query && visibleItems.length === 0 && (
        <EmptyView
          title="No Threads content available for that search"
          subtitle="Please try a different keyword or topic."
          contained
          top="300px"
        />
      )}
      <div className={threadsStyles.content}>
        <ScrollListener emitTreshold={SCROLL_THRESHOLD} scrollElement={scrollElementRef.current} onScroll={onLoadNextPage}>
          <MasonryGrid key={query + threadsType}>
            {visibleItems.map(item => (
              <AnyCard
                key={item.id}
                content={item}
                children={item}
              />
            )).concat(loadingCards, emptyCards)}
          </MasonryGrid>
        </ScrollListener>
        {loading && (
          <div className={threadsStyles['loading-overlay']} data-testid="threads-loading-overlay">
            <Loader size={LOADER_SIZE} className={threadsStyles.loader} />
            <p className={threadsStyles['loading-message']}>
              We are retrieving Threads TOP content
            </p>
          </div>
        )}
      </div>
    </div>
  )
}

export default SearchThreadsRoute
